"use client";

import * as React from "react";
import { ArrowLeft, ArrowRight, ShoppingBag } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  per_page: 8
};
const wp_query = new WP_Query(wp_query_params);
function formatPrice(price: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(price);
}
function RelatedProductsCarousel() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div data-section-id="183797" className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="aspect-[4/5] animate-pulse rounded-lg bg-muted/50" />)}
      </div>;
  }
  if (error) {
    return <div className="flex h-40 items-center justify-center rounded-lg bg-muted/30 text-muted-foreground">
        <p className="font-sans">Não foi possível carregar os equipamentos.</p>
      </div>;
  }
  if (!products || products.length === 0) {
    return null;
  }
  return <Carousel opts={{
    align: "start",
    loop: true
  }} className="w-full">
      <CarouselContent className="-ml-4 md:-ml-6">
        {products.map((product, i) => <CarouselItem key={product.id} data-index={i} className="pl-4 md:pl-6 basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/4">
            <div className="group relative flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xs transition-all duration-300 hover:shadow-md">
              {}
              <div className="relative aspect-[4/5] w-full overflow-hidden bg-muted">
                <Link to={product.link} className="absolute inset-0 z-0">
                  <span className="sr-only">Ver detalhes de {product.title}</span>
                </Link>
                
                {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                    <ShoppingBag className="h-12 w-12 opacity-20" />
                  </div>}

                {}
                <div className="absolute inset-x-0 bottom-0 z-10 translate-y-full p-4 transition-transform duration-300 ease-out group-hover:translate-y-0">
                  <AddToCartButton productId={product.id} className="w-full" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary-600 rounded-lg py-2.5 font-default font-bold shadow-2xs" text="Ver Detalhes" showQuantity={false} />
                </div>
              </div>

              {}
              <div className="flex flex-grow flex-col p-4">
                <Link to={product.link} className="group-hover:text-primary transition-colors">
                  <h3 className="line-clamp-2 text-xl font-default font-bold text-foreground">
                    {product.title}
                  </h3>
                </Link>
                
                <div className="mt-auto pt-3 font-mono text-[12px]">
                  {product.discounted_price ? <div className="flex items-baseline gap-2">
                      <span className="text-lg font-bold text-primary">
                        {formatPrice(product.discounted_price)}
                      </span>
                      <span className="text-xs text-muted-foreground line-through opacity-60">
                        {formatPrice(product.price)}
                      </span>
                    </div> : <span className="text-lg font-bold text-foreground">
                      {formatPrice(product.price)}
                    </span>}
                </div>
              </div>
            </div>
          </CarouselItem>)}
      </CarouselContent>
      
      {}
      <div className="hidden md:block">
        <CarouselPrevious className="border-border bg-background/90 text-primary hover:bg-primary hover:text-primary-foreground transition-colors" />
        <CarouselNext className="border-border bg-background/90 text-primary hover:bg-primary hover:text-primary-foreground transition-colors" />
      </div>
    </Carousel>;
}
export default function RelatedProducts() {
  return <section id="equipamentos-e-linhas-de-produto" className="w-full bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-12 lg:px-16">
        <div className="mb-8 flex items-center justify-between">
          <h2 className="font-default text-3xl font-bold uppercase tracking-tight text-foreground">
            Equipamentos e Linhas de Produto
          </h2>
        </div>
        
        <EcommerceProductsProvider wp_query={{
        per_page: 8
      }}>
          <RelatedProductsCarousel />
        </EcommerceProductsProvider>
      </div>
    </section>;
}